import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  SupportAnswer,
  SupportAskEnvelope,
  SupportAskResponseBody,
  Turn,
} from "../../interfaces/SupportObject";
import { postRequest } from "../http/httpRequest";
import { extractApiErrorMessage, fetchOrThrowFriendly } from "../helpers/ApiErrorHelper";
import { convertSupportAnswer } from "../converters/backendToUi/supportConvertor";

// Support Agent — same bearer/JWT as other product endpoints.
const BEARER = true;
const FALLBACK_ERROR = "Support is temporarily unavailable. Please try again.";

/** True when either shape's error field is actually populated — `null`,
 *  `undefined` and empty arrays all mean "no error", not just `null`. */
function hasEnvelopeError(data: SupportAskEnvelope | null): boolean {
  if (!data) return false;
  if (Array.isArray(data.errors) && data.errors.length > 0) return true;
  const info = data.errorInformation;
  if (Array.isArray(info)) return info.length > 0;
  return !!info;
}

/** Pulls the answer element out of whichever shape the backend actually sent. */
function extractResponseBody(data: SupportAskEnvelope | null): SupportAskResponseBody | undefined {
  if (!data) return undefined;
  if (data.responseBody?.[0]) return data.responseBody[0];
  if (Array.isArray(data.contents)) return data.contents[0];
  if (data.contents) return data.contents;
  return undefined;
}

/**
 * POST /api/v1/support/ask.
 *
 * Shape-tolerant on purpose — see the header comment on SupportAskEnvelope.
 * The staged-changes doc specified the NineBusinessRs envelope
 * ({status, errors, responseBody}), but the backend as observed in production
 * returns the legacy envelope instead ({generalProcessingStatus, errorInformation,
 * contents}), with `generalProcessingStatus: null` even on success — so this
 * can't gate on an explicit "SUCCESS" status. Success is instead: no populated
 * error field, and a real answer body somewhere. Both hasEnvelopeError() and
 * extractResponseBody() check both shapes, so nothing here needs to change if
 * the backend is later aligned to the documented shape.
 *
 * Either shape can report validation (400) / server (500) failures INSIDE an
 * HTTP 200 body — `response.ok` is not a reliable success signal on its own,
 * which is why the error check runs before it.
 */
async function askSupport(question: string, history: Turn[]): Promise<SupportAnswer> {
  const response = await fetchOrThrowFriendly(() =>
    postRequest(ServiceEndpoints.SUPPORT_ASK, { question, history }, BEARER)
  );

  // Parse unconditionally — on this endpoint the failure detail lives in the
  // envelope even when the HTTP status itself is 200.
  const data = (await response.json().catch(() => null)) as SupportAskEnvelope | null;

  // 1) Envelope-reported failure (validation/server error, arrives as HTTP 200).
  if (hasEnvelopeError(data)) {
    throw new Error(extractApiErrorMessage(data) ?? FALLBACK_ERROR);
  }

  // 2) Transport/infra failure that never produced a usable envelope
  //    (e.g. a gateway 5xx, or 401 before the request reached the controller).
  if (!response.ok) {
    throw new Error(extractApiErrorMessage(data) ?? FALLBACK_ERROR);
  }

  // 3) Success requires an actual answer element, not just a 200.
  const body = extractResponseBody(data);
  if (!body) {
    throw new Error(extractApiErrorMessage(data) ?? FALLBACK_ERROR);
  }

  return convertSupportAnswer(body);
}

const SupportApiService = { askSupport };

export default SupportApiService;
